// Pure section-building for the Catalog list: filter the exercise catalog
// by search query + region + equipment, bucket the matches by muscle
// region in schema-enum order, sort each bucket by canonical EN name,
// and apply the collapse state. Lifted to @heraion/ui (Catalog surface
// lift) — the filtering/grouping logic renders identically on mobile + the
// web-rnw console and stays unit-testable without a render runner.
import {
  MuscleRegion,
  allLocalizedStrings,
  exerciseRegion,
  normalizeSearch,
  type EquipmentKind as EquipmentKindT,
  type Exercise,
  type MuscleRegion as MuscleRegionT,
} from "@heraion/shared";

// Shared normalizer (@heraion/shared) — one search semantics everywhere.
const norm = normalizeSearch;

// Group order mirrors the web's catalog: schema enum order first, then
// "other" for legacy / region-less rows. Keeping the keys identical to
// the web means future surfacing of stored "user collapsed X group"
// preferences can sync across surfaces.
export const REGION_ORDER: ReadonlyArray<MuscleRegionT | "other"> = [
  ...MuscleRegion.options,
  "other",
];

export type Section = {
  key: MuscleRegionT | "other";
  data: Exercise[];
};

export function computeCatalogSections(args: {
  items: Exercise[] | null;
  query: string;
  regions: Set<MuscleRegionT>;
  equipment: Set<EquipmentKindT>;
  collapsed: Set<string>;
}): Section[] {
  const { items, query, regions, equipment, collapsed } = args;
  if (items === null) return [];
  const q = norm(query.trim());
  const filtersActive =
    q.length > 0 || regions.size > 0 || equipment.size > 0;

  const matches = items
    .filter((ex) => {
      if (regions.size === 0) return true;
      // Region is rarely stored on the exercise row; derive it from
      // the primary muscle so the chip filter actually narrows.
      const region = exerciseRegion(ex);
      return region ? regions.has(region) : false;
    })
    .filter((ex) => {
      if (equipment.size === 0) return true;
      return ex.equipment ? equipment.has(ex.equipment) : false;
    })
    .filter((ex) => {
      if (q === "") return true;
      // Multi-locale search: match across every populated locale of
      // the exercise name so a user typing in their own language
      // finds the row even when only EN is filled.
      if (norm(allLocalizedStrings(ex.name)).includes(q)) return true;
      const aliases = ex.aliases ?? [];
      return aliases.some((a) => norm(a).includes(q));
    });

  const byRegion = new Map<MuscleRegionT | "other", Exercise[]>();
  for (const ex of matches) {
    // Same derivation as the filter so the section a row appears in
    // matches the chip that picks it out. Without this, every row
    // bucketed as "other" because Exercise.region is unset.
    const key: MuscleRegionT | "other" = exerciseRegion(ex) ?? "other";
    const list = byRegion.get(key);
    if (list) list.push(ex);
    else byRegion.set(key, [ex]);
  }
  const out: Section[] = [];
  for (const region of REGION_ORDER) {
    const list = byRegion.get(region);
    if (!list || list.length === 0) continue;
    // Sort on the canonical EN name so list ordering stays stable
    // when the user toggles language at runtime.
    list.sort((a, b) => a.name.en.localeCompare(b.name.en));
    // When any filter is active force-expand so matches are visible
    // without an extra tap. Returning data: [] when collapsed keeps
    // the SectionList header rendering and the chevron in sync.
    const isCollapsed = !filtersActive && collapsed.has(region);
    out.push({ key: region, data: isCollapsed ? [] : list });
  }
  return out;
}
